/**
 * FAQ entries for the help panel (Phase 2 — Step 2.16).
 */

import type { FAQEntry } from '@abs/shared-types';

export const FAQ_DATA: FAQEntry[] = [
  {
    id: 'faq-1',
    question: 'How is my attendance budget calculated?',
    answer: 'Your attendance budget is the total number of absences allowed before penalties kick in, as defined by your course\'s attendance policy. The budget accounts for grace allowances (free absences) and any tardy-to-absence conversions.',
    category: 'Budget',
  },
  {
    id: 'faq-2',
    question: 'What is a grace allowance?',
    answer: 'A grace allowance is a number of free absences that don\'t count toward penalties. For example, if you have 2 unexcused grace absences, your first 2 unexcused absences won\'t trigger any penalty tiers.',
    category: 'Policy',
  },
  {
    id: 'faq-3',
    question: 'How do tardy arrivals affect my absence count?',
    answer: 'If your policy has tardy conversion enabled, a certain number of tardies (late arrivals or early departures) count as one full absence. For example, "3 tardies = 1 absence" means every 3 tardy events adds 1 to your penalty absence count.',
    category: 'Policy',
  },
  {
    id: 'faq-4',
    question: 'What does "At Risk" mean?',
    answer: '"At Risk" means you are within 1 absence of triggering the next penalty tier. This is a warning that one more absence could result in a grade deduction or letter grade drop.',
    category: 'Risk',
  },
  {
    id: 'faq-5',
    question: 'What is the difference between excused and unexcused absences?',
    answer: 'Excused absences typically have documentation (doctor\'s note, university event) and may have separate grace allowances. Unexcused absences count toward penalties more quickly. Your policy defines how each type is handled.',
    category: 'Absences',
  },
  {
    id: 'faq-6',
    question: 'How is my grade impact calculated?',
    answer: 'Grade impact is computed by applying the active penalty tier deductions to your base grade (the grade you would have without attendance penalties). Each tier specifies a point deduction and/or letter grade drop.',
    category: 'Grades',
  },
  {
    id: 'faq-7',
    question: 'What does the recovery path show?',
    answer: 'The recovery path shows whether you can return to a safe attendance level by attending all remaining sessions. It calculates the sessions needed, target percentage, and whether recovery is mathematically possible.',
    category: 'Budget',
  },
  {
    id: 'faq-8',
    question: 'Can I simulate future absences?',
    answer: 'Yes! The What-If planning tool (Phase 3) lets you simulate scenarios like "What if I miss 2 more classes?" to see how it would affect your grade and risk level before you decide.',
    category: 'Planning',
  },
  {
    id: 'faq-9',
    question: 'How do I back up my data?',
    answer: 'Click the Export/Import button in the header to download a JSON backup of all your data. You can restore from this file at any time. Automatic backups are also saved every 24 hours.',
    category: 'Data',
  },
  {
    id: 'faq-10',
    question: 'Does my data sync across devices?',
    answer: 'Currently, Absence Budget stores all data locally in your browser. Use the export/import feature to transfer data between devices. Cloud sync is planned for a future release.',
    category: 'Data',
  },
  {
    id: 'faq-11',
    question: 'What is partial absence value?',
    answer: 'Some policies count late arrivals or early departures as a fraction of a full absence. For example, a partial value of 0.5 means 2 late arrivals = 1 full absence. This is configured in your attendance policy.',
    category: 'Policy',
  },
  {
    id: 'faq-12',
    question: 'What is credit-hour weighting?',
    answer: 'Courses with more credit hours may have proportionally higher penalties. For example, a 4-credit lab course may apply a 1.33x multiplier to grade deductions compared to a standard 3-credit course.',
    category: 'Grades',
  },
  {
    id: 'faq-13',
    question: 'How do I set up a pass/fail course?',
    answer: 'When creating or editing a course, enable the "Pass/Fail" toggle and set the passing threshold percentage. The app will show PASS/FAIL status instead of letter grades.',
    category: 'Setup',
  },
];
